import { TaskPlaceHolder } from "../constants";
import {
  ProductPhase,
  ProductTopic,
  TaskTemplateId,
  TaskTemplate,
  DownloadablePdfUrl,
  CTA,
} from "../types";
import { getCurrentTaxYear } from "@modules/tax/utils";

// The HIGHER the Sort Index, the HIGHER up on the LIST by DEFAULT.
// The order of these tasks is how they will appear in the lists by default.
const taskTemplates: TaskTemplate[] = [
  // -------------------------------------------------
  // ------- PHASE 1 "PRE-ONBOARDING" TASKS ----------
  // -------------------------------------------------
  {
    name: "Upload investment statements",
    description:
      "Upload your investment statement from your taxable accounts to help us identify tax saving opportunities.",
    callToAction: CTA.UPLOAD_INVESTMENT_DOCUMENTS,
    estimatedCompletionTime: 5,
    productPhase: ProductPhase.PHASE_1,
    templateId: TaskTemplateId.UPLOAD_INVESTMENT_STATEMENTS_PHASE_1,
    wealthDiagnostic: true,
    wealthManagement: true,
    sortIndex: 0,
    isInitialTask: false,
    isPreOnboardingTask: true,
  },
  {
    name: "Share Tax information with your advisor",
    description:
      "Upload your latest tax documents so we can begin our analysis.",
    callToAction: CTA.UPLOAD_TAX_DOCUMENTS,
    productPhase: ProductPhase.PHASE_1,
    templateId: TaskTemplateId.SHARE_TAX_INFORMATION_WITH_YOUR_ADVISOR,
    wealthDiagnostic: true,
    wealthManagement: true,
    sortIndex: 0,
    isInitialTask: false,
    isPreOnboardingTask: true,
  },
  // --------------------------------
  // ------- PHASE 1 TASKS ----------
  // --------------------------------
  {
    name: "Link bank, debt, real estate, and unmanaged investment accounts",
    description:
      "Go to the Other Accounts Tab to link all checking, savings, real estate assets, credit cards, mortgages, student loans, other liabilities, and investment accounts that Earned does not manage. This helps us provide an accurate Comprehensive Financial Plan.",
    callToAction: CTA.CONNECT_INVESTMENT_ACCOUNT,
    estimatedCompletionTime: 10,
    productPhase: ProductPhase.PHASE_1,
    templateId: TaskTemplateId.LINK_ACCOUNTS,
    wealthDiagnostic: true,
    wealthManagement: true,
    sortIndex: 0,
    // Not auto-assigned during onboarding (see #2270); kept available so
    // advisors can assign it from the "Assign Tasks" picklist.
    isInitialTask: false,
  },
  {
    name: `Update personal information ${TaskPlaceHolder.USER_NAME}`,
    description:
      "Answer a few questions to ensure we have all of your basic information.",
    callToAction: CTA.OPEN_WIZARD,
    estimatedCompletionTime: 10,
    productPhase: ProductPhase.PHASE_1,
    templateId: TaskTemplateId.UPDATE_PERSONAL_INFORMATION,
    wealthDiagnostic: true,
    wealthManagement: true,
    sortIndex: 0,
    isInitialTask: false,
    isCoClientTask: false,
  },
  {
    name: `Update personal information ${TaskPlaceHolder.USER_NAME}`,
    description:
      "Answer a few questions to ensure we have all of your basic information.",
    callToAction: CTA.OPEN_WIZARD,
    estimatedCompletionTime: 10,
    productPhase: ProductPhase.PHASE_1,
    templateId: TaskTemplateId.UPDATE_PERSONAL_INFORMATION_COCLIENT,
    wealthDiagnostic: true,
    wealthManagement: true,
    sortIndex: 0,
    isInitialTask: false,
    isCoClientTask: true,
  },
  {
    name: "Upload a copy of a voided check",
    description:
      "Write 'VOID' across the front of a blank check and upload a copy to the Banking folder in your Document Vault. We'll use it to link your Fidelity accounts and your checking account for distributions or contributions. Your signature will be requested before the link is established.",
    callToAction: CTA.UPLOAD_BANKING_DOCUMENTS,
    productPhase: ProductPhase.PHASE_1,
    templateId: TaskTemplateId.UPLOAD_VOIDED_CHECK,
    wealthDiagnostic: true,
    wealthManagement: true,
    sortIndex: 0,
    isInitialTask: false,
  },
  {
    name: "Set up Kickoff Advisory Meeting",
    description:
      "Set up a meeting with your advisor to begin executing on your financial and career goals.",
    callToAction: CTA.SCHEDULE_MEETING_ADVISOR,
    productPhase: ProductPhase.PHASE_1,
    templateId: TaskTemplateId.SETUP_KICKOFF_ADVISORY_MEETING,
    wealthDiagnostic: true,
    wealthManagement: true,
    sortIndex: 0,
    isInitialTask: false,
  },
  {
    name: `Sign Wealth Diagnostic Agreement ${TaskPlaceHolder.USER_NAME}`,
    description:
      "Execute Wealth Diagnostic Agreement to commence engagement with Earned Wealth.",
    callToAction: CTA.DOCUSIGN_WDA,
    estimatedCompletionTime: 1,
    productPhase: ProductPhase.PHASE_1,
    templateId: TaskTemplateId.SIGN_WEALTH_DIAGNOSTIC_AGREEMENT,
    wealthDiagnostic: true,
    wealthManagement: false,
    sortIndex: 0,
    isInitialTask: false,
    isDocusignTask: true,
  },
  {
    name: `Sign Wealth Diagnostic Agreement ${TaskPlaceHolder.USER_NAME}`,
    description:
      "Execute Wealth Diagnostic Agreement to commence engagement with Earned Wealth.",
    callToAction: CTA.DOCUSIGN_WDA_COCLIENT,
    estimatedCompletionTime: 1,
    productPhase: ProductPhase.PHASE_1,
    templateId: TaskTemplateId.SIGN_WEALTH_DIAGNOSTIC_AGREEMENT_COCLIENT,
    wealthDiagnostic: true,
    wealthManagement: false,
    sortIndex: 0,
    isInitialTask: false,
    isCoClientTask: true,
    isDocusignTask: true,
  },
  {
    name: `Sign Wealth Management Agreement ${TaskPlaceHolder.USER_NAME}`,
    description:
      "Execute Wealth Management Agreement to commence an ongoing engagement with Earned Wealth.",
    callToAction: CTA.DOCUSIGN_WMA,
    estimatedCompletionTime: 1,
    productPhase: ProductPhase.PHASE_1,
    templateId: TaskTemplateId.SIGN_WEALTH_MANAGEMENT_AGREEMENT,
    wealthDiagnostic: false,
    wealthManagement: true,
    sortIndex: 0,
    isInitialTask: false,
    isDocusignTask: true,
  },
  {
    name: `Sign Wealth Management Agreement ${TaskPlaceHolder.USER_NAME}`,
    description:
      "Execute Wealth Management Agreement to commence an ongoing engagement with Earned Wealth.",
    callToAction: CTA.DOCUSIGN_WMA_COCLIENT,
    estimatedCompletionTime: 1,
    productPhase: ProductPhase.PHASE_1,
    templateId: TaskTemplateId.SIGN_WEALTH_MANAGEMENT_AGREEMENT_COCLIENT,
    wealthDiagnostic: false,
    wealthManagement: true,
    sortIndex: 0,
    isInitialTask: false,
    isCoClientTask: true,
    isDocusignTask: true,
  },
  {
    name: `Upload Government-Issued ID ${TaskPlaceHolder.USER_NAME}`,
    description:
      "Please upload a copy of your government-issued ID (e.g., driver’s license). To adhere to SEC regulations, this is required before we can open any of your accounts.",
    callToAction: CTA.UPLOAD_GENERAL_DOCUMENTS,
    estimatedCompletionTime: 1,
    productPhase: ProductPhase.PHASE_1,
    templateId: TaskTemplateId.UPLOAD_GOV_ISSUED_ID,
    wealthDiagnostic: true,
    wealthManagement: true,
    sortIndex: 0,
    isInitialTask: false,
  },
  {
    name: `Upload Government-Issued ID ${TaskPlaceHolder.USER_NAME}`,
    description:
      "Please upload a copy of your government-issued ID (e.g., driver’s license). To adhere to SEC regulations, this is required before we can open any of your accounts.",
    callToAction: CTA.UPLOAD_GENERAL_DOCUMENTS,
    estimatedCompletionTime: 1,
    productPhase: ProductPhase.PHASE_1,
    templateId: TaskTemplateId.UPLOAD_GOV_ISSUED_ID_COCLIENT,
    wealthDiagnostic: true,
    wealthManagement: true,
    sortIndex: 0,
    isInitialTask: false,
    isCoClientTask: true,
  },
  // --------------------------------
  // ------- PHASE 2 TASKS ----------
  // ------- ORDERED BY WEALTH DOMAIN
  // --------------------------------
  //
  // --------------------------------
  // ------- FINANCIAL PLAN ---------
  // --------------------------------
  {
    name: "Tell us about your financial goals",
    description: `Complete and upload the PDF to the "Plans and proposals" folder in your document vault.`,
    estimatedCompletionTime: 45,
    callToAction: CTA.DOWNLOAD_PDF,
    productPhase: ProductPhase.PHASE_2,
    productTopic: ProductTopic.FINANCIAL_PLAN,
    templateId: TaskTemplateId.MGP_QUESTIONNAIRE,
    wealthDiagnostic: true,
    wealthManagement: true,
    downloadablePdfUrl: DownloadablePdfUrl.MGP_QUESTIONNAIRE,
    sortIndex: 0,
    isInitialTask: false,
  },
  {
    name: "Tell us about your financial goals",
    description: `Complete and upload the PDF to the "Plans and proposals" folder in your document vault.`,
    estimatedCompletionTime: 45,
    callToAction: CTA.DOWNLOAD_PDF,
    productPhase: ProductPhase.PHASE_2,
    productTopic: ProductTopic.FINANCIAL_PLAN,
    templateId: TaskTemplateId.MGP_QUESTIONNAIRE_COCLIENT,
    wealthDiagnostic: true,
    wealthManagement: true,
    downloadablePdfUrl: DownloadablePdfUrl.MGP_QUESTIONNAIRE_COCLIENT,
    sortIndex: 0,
    isInitialTask: false,
    isCoClientTask: true,
  },
  {
    name: "Tell us about your financial goals",
    description: `Please fill out information about your financial goals to help your advisor create your financial plan.`,
    estimatedCompletionTime: 30,
    callToAction: CTA.OPEN_WIZARD,
    productPhase: ProductPhase.PHASE_2,
    productTopic: ProductTopic.FINANCIAL_PLAN,
    templateId: TaskTemplateId.MGP_QUESTIONNAIRE_WIZARD,
    wealthDiagnostic: true,
    wealthManagement: true,
    sortIndex: 0,
    isInitialTask: false,
  },
  {
    name: "Talk to Advisory team to begin executing your Financial Plan",
    description:
      "Set up a meeting with one of our advisors, who will help you setup your financial plan and begin executing toward your financial and career goals.",
    callToAction: CTA.SCHEDULE_MEETING_ADVISOR,
    productPhase: ProductPhase.PHASE_2,
    productTopic: ProductTopic.FINANCIAL_PLAN,
    templateId: TaskTemplateId.TALK_TO_ADVISORY_TEAM_TO_BEGIN,
    wealthDiagnostic: true,
    wealthManagement: true,
    sortIndex: 0,
    isInitialTask: false,
  },
  {
    name: "Set up next Advisory Meeting",
    description:
      "Set up your next meeting with your advisor to continue executing on your financial and career goals.",
    callToAction: CTA.SCHEDULE_MEETING_ADVISOR,
    productPhase: ProductPhase.PHASE_2,
    productTopic: ProductTopic.FINANCIAL_PLAN,
    templateId: TaskTemplateId.SETUP_NEXT_ADVISORY_MEETING,
    wealthDiagnostic: true,
    wealthManagement: true,
    sortIndex: 0,
    isInitialTask: false,
  },
  {
    name: "Upload your employee benefits summary",
    description:
      "This allows us to have a better understanding of the benefits available to you and how this can be factored into your financial planning",
    callToAction: CTA.UPLOAD_INSURANCE_DOCUMENTS,
    estimatedCompletionTime: 5,
    productPhase: ProductPhase.PHASE_2,
    productTopic: ProductTopic.FINANCIAL_PLAN,
    templateId: TaskTemplateId.UPLOAD_EMPLOYEE_BENEFITS_SUMMARY,
    wealthDiagnostic: true,
    wealthManagement: true,
    sortIndex: 0,
    isInitialTask: false,
  },
  {
    name: "Upload your most recent paystub",
    description:
      "This allows us to see your deferrals, matching and matching type along with withholding and other employee benefits",
    callToAction: CTA.UPLOAD_INCOME_DOCUMENTS,
    estimatedCompletionTime: 5,
    productPhase: ProductPhase.PHASE_2,
    productTopic: ProductTopic.FINANCIAL_PLAN,
    templateId: TaskTemplateId.UPLOAD_PAYSTUB,
    wealthDiagnostic: true,
    wealthManagement: true,
    sortIndex: 0,
    isInitialTask: false,
  },
  {
    name: "Upload your most recent mortgage statement",
    description: "",
    callToAction: CTA.UPLOAD_REAL_ESTATE_DOCUMENTS,
    estimatedCompletionTime: 5,
    productPhase: ProductPhase.PHASE_2,
    productTopic: ProductTopic.FINANCIAL_PLAN,
    templateId: TaskTemplateId.UPLOAD_MORTGAGE_STATEMENT,
    wealthDiagnostic: true,
    wealthManagement: true,
    sortIndex: 0,
    isInitialTask: false,
  },
  {
    name: "Upload your Social Security statement",
    description:
      "This allows us to optimize your retirement income strategy. Statements can be accessed at https://www.ssa.gov/myaccount/statement.html",
    callToAction: CTA.UPLOAD_INCOME_DOCUMENTS,
    estimatedCompletionTime: 5,
    productPhase: ProductPhase.PHASE_2,
    productTopic: ProductTopic.FINANCIAL_PLAN,
    templateId: TaskTemplateId.UPLOAD_SOCIAL_SECURITY_STATEMENT,
    wealthDiagnostic: true,
    wealthManagement: true,
    sortIndex: 0,
    isInitialTask: false,
  },
  // --------------------------------
  // --------- INVESTMENTS ----------
  // --------------------------------
  {
    name: "Upload investment statements",
    description:
      "Upload your investment statement from your taxable accounts to help us identify tax saving opportunities.",
    callToAction: CTA.UPLOAD_INVESTMENT_DOCUMENTS,
    estimatedCompletionTime: 5,
    productPhase: ProductPhase.PHASE_2,
    productTopic: ProductTopic.INVESTMENT_PLAN,
    templateId: TaskTemplateId.UPLOAD_INVESTMENT_STATEMENTS,
    wealthDiagnostic: true,
    wealthManagement: true,
    sortIndex: 0,
    isInitialTask: false,
  },
  {
    name: `Risk Tolerance Questionnaire ${TaskPlaceHolder.USER_NAME}`,
    description: `Answer questions to determine how much risk you are willing to take in your portfolio.`,
    callToAction: CTA.OPEN_WIZARD,
    estimatedCompletionTime: 5,
    productPhase: ProductPhase.PHASE_2,
    productTopic: ProductTopic.INVESTMENT_PLAN,
    templateId: TaskTemplateId.RISK_TOLERANCE_QUESTIONNAIRE,
    wealthDiagnostic: true,
    wealthManagement: true,
    sortIndex: 0,
    isInitialTask: false,
    isCoClientTask: false,
  },
  {
    name: `Risk Tolerance Questionnaire ${TaskPlaceHolder.USER_NAME}`,
    description: `Answer questions to determine how much risk you are willing to take in your portfolio.`,
    callToAction: CTA.OPEN_WIZARD,
    estimatedCompletionTime: 5,
    productPhase: ProductPhase.PHASE_2,
    productTopic: ProductTopic.INVESTMENT_PLAN,
    templateId: TaskTemplateId.RISK_TOLERANCE_QUESTIONNAIRE_COCLIENT,
    wealthDiagnostic: true,
    wealthManagement: true,
    sortIndex: 0,
    isInitialTask: false,
    isCoClientTask: true,
  },
  {
    name: "Link accounts in Pontera",
    description:
      "Follow the email instructions from Pontera to link your investment accounts. Pontera is the platform we use to manage your 401(k) and 403(b) accounts.",
    productPhase: ProductPhase.PHASE_2,
    productTopic: ProductTopic.INVESTMENT_PLAN,
    templateId: TaskTemplateId.LINK_ACCOUNTS_IN_PONTERA,
    wealthDiagnostic: false,
    wealthManagement: true,
    sortIndex: 0,
    isInitialTask: false,
  },
  {
    name: "Open your accounts",
    description:
      "Docusign your account opening documents that were sent to your email",
    estimatedCompletionTime: 1,
    productPhase: ProductPhase.PHASE_2,
    productTopic: ProductTopic.INVESTMENT_PLAN,
    templateId: TaskTemplateId.WEALTHSCAPE_ACCOUNT_OPENING,
    wealthDiagnostic: false,
    wealthManagement: true,
    sortIndex: 0,
    isInitialTask: false,
  },
  {
    name: "Upload illiquid asset information",
    description:
      "Upload illiquid asset statements such (e.g., real estate holdings) to your document vault.",
    callToAction: CTA.UPLOAD_INVESTMENT_DOCUMENTS,
    estimatedCompletionTime: 5,
    productPhase: ProductPhase.PHASE_2,
    productTopic: ProductTopic.INVESTMENT_PLAN,
    templateId: TaskTemplateId.UPLOAD_ILLIQUID_ASSET_INFORMATION,
    wealthDiagnostic: true,
    wealthManagement: true,
    sortIndex: 0,
    isInitialTask: false,
  },
  // --------------------------------
  // --------- TAX CENTER ---------
  // --------------------------------
  {
    name: "Upload Tax Document",
    description: "Please upload this document.",
    callToAction: CTA.UPLOAD_TAX_CENTER_DOCUMENT,
    estimatedCompletionTime: 5,
    productPhase: ProductPhase.PHASE_2,
    productTopic: ProductTopic.TAX_CENTER,
    templateId: TaskTemplateId.UPLOAD_TAX_CENTER_DOCUMENT,
    wealthDiagnostic: true,
    wealthManagement: true,
    sortIndex: 0,
    isInitialTask: false,
  },
  {
    name: `Complete taxes for ${getCurrentTaxYear()} `,
    description:
      "Please complete document uploads so your team can start working on your returns.",
    callToAction: CTA.NAVIGATE_TO_TAX_CENTER,
    productPhase: ProductPhase.PHASE_2,
    productTopic: ProductTopic.TAX_CENTER,
    templateId: TaskTemplateId.NAVIGATE_TO_TAX_CENTER,
    wealthDiagnostic: true,
    wealthManagement: true,
    sortIndex: 0,
    isInitialTask: false,
  },
  // --------------------------------
  // --------- TAX PLANNING ---------
  // --------------------------------
  {
    name: "Upload tax returns",
    description:
      "Upload the last two years of tax returns to your document vault.",
    callToAction: CTA.UPLOAD_TAX_DOCUMENTS,
    estimatedCompletionTime: 5,
    productPhase: ProductPhase.PHASE_2,
    productTopic: ProductTopic.TAX_PLAN,
    templateId: TaskTemplateId.UPLOAD_TAX_RETURNS,
    wealthDiagnostic: true,
    wealthManagement: true,
    sortIndex: 0,
    isInitialTask: false,
  },
  {
    name: "Upload your most recent property tax statement",
    description: "",
    callToAction: CTA.UPLOAD_TAX_DOCUMENTS,
    estimatedCompletionTime: 5,
    productPhase: ProductPhase.PHASE_2,
    productTopic: ProductTopic.TAX_PLAN,
    templateId: TaskTemplateId.UPLOAD_PROPERTY_TAX_STATEMENT,
    wealthDiagnostic: true,
    wealthManagement: true,
    sortIndex: 0,
    isInitialTask: false,
  },
  // --------------------------------
  // -------- CAREER ADVICE ---------
  // --------------------------------
  {
    name: "Upload employment contract (if applicable)",
    description: `Upload your employment contract so we can run a custom compensation benchmarking report for you.`,
    callToAction: CTA.UPLOAD_CREDENTIALING_DOCUMENTS,
    productPhase: ProductPhase.PHASE_2,
    productTopic: ProductTopic.CAREER,
    templateId: TaskTemplateId.UPLOAD_EMPLOYMENT_CONTRACT,
    wealthDiagnostic: true,
    wealthManagement: true,
    sortIndex: 0,
    isInitialTask: false,
  },
  // --------------------------------
  // -------- INSURANCE PLAN --------
  // --------------------------------
  {
    name: `Update disability insurance information ${TaskPlaceHolder.USER_NAME}`,
    description:
      "Answer questions about your disability insurance coverage to help us compare options for you.",
    callToAction: CTA.OPEN_WIZARD,
    estimatedCompletionTime: 5,
    productPhase: ProductPhase.PHASE_2,
    productTopic: ProductTopic.INSURANCE_PLAN,
    templateId: TaskTemplateId.UPDATE_DISABILITY_INSURANCE_INFORMATION,
    wealthDiagnostic: true,
    wealthManagement: true,
    sortIndex: 0,
    isInitialTask: false,
    isCoClientTask: false,
  },
  {
    name: `Update disability insurance information ${TaskPlaceHolder.USER_NAME}`,
    description:
      "Answer questions about your disability insurance coverage to help us compare options for you.",
    callToAction: CTA.OPEN_WIZARD,
    estimatedCompletionTime: 5,
    productPhase: ProductPhase.PHASE_2,
    productTopic: ProductTopic.INSURANCE_PLAN,
    templateId: TaskTemplateId.UPDATE_DISABILITY_INSURANCE_INFORMATION_COCLIENT,
    wealthDiagnostic: true,
    wealthManagement: true,
    sortIndex: 0,
    isInitialTask: false,
    isCoClientTask: true,
  },
  {
    name: `Update term life insurance information ${TaskPlaceHolder.USER_NAME}`,
    description:
      "Answer questions about your term life insurance coverage to help us compare options for you.",
    callToAction: CTA.OPEN_WIZARD,
    estimatedCompletionTime: 5,
    productPhase: ProductPhase.PHASE_2,
    productTopic: ProductTopic.INSURANCE_PLAN,
    templateId: TaskTemplateId.UPDATE_TERM_LIFE_INSURANCE_INFORMATION,
    wealthDiagnostic: true,
    wealthManagement: true,
    sortIndex: 0,
    isInitialTask: false,
    isCoClientTask: false,
  },
  {
    name: `Update term life insurance information ${TaskPlaceHolder.USER_NAME}`,
    description:
      "Answer questions about your term life insurance coverage to help us compare options for you.",
    callToAction: CTA.OPEN_WIZARD,
    estimatedCompletionTime: 5,
    productPhase: ProductPhase.PHASE_2,
    productTopic: ProductTopic.INSURANCE_PLAN,
    templateId: TaskTemplateId.UPDATE_TERM_LIFE_INSURANCE_INFORMATION_COCLIENT,
    wealthDiagnostic: true,
    wealthManagement: true,
    sortIndex: 0,
    isInitialTask: false,
    isCoClientTask: true,
  },
  {
    name: "Update home insurance information",
    description: `Complete and upload the PDF to the "Insurance" folder in your document vault. Answer questions about your home insurance coverage to help us compare options for you.`,
    callToAction: CTA.DOWNLOAD_PDF,
    estimatedCompletionTime: 10,
    productPhase: ProductPhase.PHASE_2,
    productTopic: ProductTopic.INSURANCE_PLAN,
    templateId: TaskTemplateId.UPDATE_HOME_INSURANCE_INFORMATION,
    wealthDiagnostic: true,
    wealthManagement: true,
    downloadablePdfUrl: DownloadablePdfUrl.HOME_INSURANCE_QUESTIONNAIRE,
    sortIndex: 0,
    isInitialTask: false,
  },
  {
    name: "Update auto insurance information",
    description: `Complete and upload the PDF to the "Insurance" folder in your document vault. Answer questions about your auto insurance coverage to help us compare options for you.`,
    callToAction: CTA.DOWNLOAD_PDF,
    estimatedCompletionTime: 5,
    productPhase: ProductPhase.PHASE_2,
    productTopic: ProductTopic.INSURANCE_PLAN,
    templateId: TaskTemplateId.UPDATE_AUTO_INSURANCE_INFORMATION,
    wealthDiagnostic: true,
    wealthManagement: true,
    downloadablePdfUrl: DownloadablePdfUrl.AUTO_INSURANCE_QUESTIONNAIRE,
    sortIndex: 0,
    isInitialTask: false,
  },
  {
    name: "Upload insurance policies for review",
    description:
      "Please upload your Life, Disability, Auto, Home, Long Term Disability and Umbrella policies. Mark this task complete when all uploads are in the document vault.",
    callToAction: CTA.UPLOAD_INSURANCE_DOCUMENTS,
    estimatedCompletionTime: 15,
    productPhase: ProductPhase.PHASE_2,
    productTopic: ProductTopic.INSURANCE_PLAN,
    templateId: TaskTemplateId.UPLOAD_INSURANCE_POLICIES,
    wealthDiagnostic: true,
    wealthManagement: true,
    sortIndex: 0,
    isInitialTask: false,
  },
  // --------------------------------
  // --------- DEBT & CASH ----------
  // --------------------------------
  {
    name: "Upload student debt statement(s)",
    description: `Upload your student debt statement(s) to the document vault.`,
    callToAction: CTA.UPLOAD_TAX_DOCUMENTS,
    productPhase: ProductPhase.PHASE_2,
    productTopic: ProductTopic.DEBT_AND_CASH,
    templateId: TaskTemplateId.UPLOAD_STUDENT_DEBT_STATEMENTS,
    wealthDiagnostic: true,
    wealthManagement: true,
    sortIndex: 0,
    isInitialTask: false,
  },
  // --------------------------------
  // ------- TRUST & ESTATE ---------
  // --------------------------------
  {
    name: "Upload estate planning documents",
    description:
      "Upload your estatement planning documents (including your will, trusts, and power-of-attorney documents) to your document vault.",
    callToAction: CTA.UPLOAD_TRUST_ESTATE_DOCUMENTS,
    estimatedCompletionTime: 30,
    productPhase: ProductPhase.PHASE_2,
    productTopic: ProductTopic.ESTATE_PLAN,
    templateId: TaskTemplateId.UPLOAD_ESTATE_PLANNING_DOCUMENTS,
    wealthDiagnostic: true,
    wealthManagement: true,
    sortIndex: 0,
    isInitialTask: false,
  },
  {
    name: "Set up your Estate Plan with Trust & Will",
    description:
      "Trust & Will is a separate company that offers a user-friendly platform. The purpose is to help you protect your legacy and ensures your future generations are secure.",
    callToAction: CTA.OPEN_URL,
    estimatedCompletionTime: 60,
    productPhase: ProductPhase.PHASE_2,
    productTopic: ProductTopic.ESTATE_PLAN,
    templateId: TaskTemplateId.SET_UP_ESTATE_PLAN_WITH_TRUST_WILL,
    wealthDiagnostic: true,
    wealthManagement: true,
    sortIndex: 0,
    isInitialTask: false,
    ctaText: "Get Started",
  },
  // ------------------------------------------------------------
  // ------- PHASE 2 + Ongoing Wealth Management TASKS ----------
  // ------------------------------------------------------------
  {
    name: "Set up next Advisory Meeting",
    description:
      "Set up your next meeting with your advisor to continue executing on your financial and career goals.",
    callToAction: CTA.SCHEDULE_MEETING_ADVISOR,
    productPhase: ProductPhase.PHASE_2,
    templateId: TaskTemplateId.SETUP_NEXT_ADVISORY_MEETING_ONGOING,
    wealthDiagnostic: true,
    wealthManagement: true,
    sortIndex: 0,
    isInitialTask: false,
    isOngoingWealthManagementTask: true,
  },
  {
    name: "Review Wealth Diagnostic plan",
    description: "Review your Wealth Diagnostic plan with your advisor.",
    callToAction: CTA.SCHEDULE_MEETING_ADVISOR,
    estimatedCompletionTime: 45,
    productPhase: ProductPhase.PHASE_2,
    templateId: TaskTemplateId.REVIEW_WEALTH_DIAGNOSTIC_PLAN,
    wealthDiagnostic: true,
    wealthManagement: false,
    sortIndex: 0,
    isInitialTask: false,
    isOngoingWealthManagementTask: true,
  },
  {
    name: "Want to continue working with us?",
    description:
      "Let us take your diganostic and help you execute the items we found to improve your financial situaiton with our white glove wealth management experience.  Join within 30 days of diagnostic completion and we'll credit your asset management fee with the diagnostic fee.",
    callToAction: CTA.SCHEDULE_MEETING_ADVISOR,
    estimatedCompletionTime: null,
    productPhase: ProductPhase.PHASE_2,
    templateId: TaskTemplateId.UPGRADE_TO_WEALTH_MANAGEMENT,
    wealthDiagnostic: true,
    wealthManagement: false,
    sortIndex: 0,
    isInitialTask: false,
    isOngoingWealthManagementTask: true,
  },
  {
    name: "Annual Checkup - Upload Tax Documents for Tax Prep",
    description:
      "Upload all necessary tax documents needed for end-of-year tax preparations.",
    callToAction: CTA.UPLOAD_TAX_DOCUMENTS,
    estimatedCompletionTime: null,
    productPhase: ProductPhase.PHASE_2,
    templateId: TaskTemplateId.ANNUAL_CHECKUP_TAX_PREP_UPLOAD,
    wealthDiagnostic: false,
    wealthManagement: true,
    sortIndex: 0,
    isInitialTask: false,
    isOngoingWealthManagementTask: true,
  },
  {
    name: "Annual Checkup - Post-April Tax Planning Review",
    description:
      "Schedule a meeting for a strategic review of your tax planning after April 15th.",
    callToAction: CTA.SCHEDULE_MEETING_ADVISOR,
    estimatedCompletionTime: null,
    productPhase: ProductPhase.PHASE_2,
    templateId: TaskTemplateId.ANNUAL_CHECKUP_TAX_PLANNING_REVIEW,
    wealthDiagnostic: false,
    wealthManagement: true,
    sortIndex: 0,
    isInitialTask: false,
    isOngoingWealthManagementTask: true,
  },
  {
    name: "Annual Checkup - Life Happens",
    description:
      "Let's review various topics including a new job or salary increase, changes in home value, acquisitions like jewelry or art, and ensuring you have adequate insurance coverage. We'll also discuss any additions to your family or individuals you're financially responsible for. Let's catch up and update your financial plan to ensure you're still on track to reaching your goals.",
    callToAction: CTA.SCHEDULE_MEETING_ADVISOR,
    estimatedCompletionTime: null,
    productPhase: ProductPhase.PHASE_2,
    templateId: TaskTemplateId.ANNUAL_CHECKUP_REVIEW,
    wealthDiagnostic: false,
    wealthManagement: true,
    sortIndex: 0,
    isInitialTask: false,
    isOngoingWealthManagementTask: true,
  },
]
  .reverse()
  .map((template, index) => {
    template.sortIndex = (1 + index) * 1000;
    return template;
  });

export { taskTemplates };
